import React, { useState, useEffect } from 'react';
import {
  X,
  MessageSquareHeart,
  Send,
  Sparkles,
  Phone,
  User,
  Moon,
  Activity,
  Utensils,
  Copy,
  Check,
  RotateCcw,
  Smartphone
} from 'lucide-react';
import { FamilyDigestSubject } from '../types';
import { generateFamilyDigestSMS } from '../services/geminiService';

interface FamilyDigestModalProps {
  subject: FamilyDigestSubject | null;
  isOpen: boolean;
  onClose: () => void;
  onSendSMS: (recipientName: string, phone: string, message: string) => void;
}

export const FamilyDigestModal: React.FC<FamilyDigestModalProps> = ({
  subject,
  isOpen,
  onClose,
  onSendSMS
}) => {
  const [message, setMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [sentSuccess, setSentSuccess] = useState<boolean>(false);

  const fetchDigest = async () => {
    if (!subject) return;
    setIsGenerating(true);
    setSentSuccess(false);
    const sms = await generateFamilyDigestSMS(subject);
    setMessage(sms);
    setIsGenerating(false);
  };

  useEffect(() => {
    if (isOpen && subject) {
      fetchDigest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subject?.id]);

  if (!isOpen || !subject) return null;

  const handleCopy = () => {
    if (!message) return;
    navigator.clipboard.writeText(message);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSend = () => {
    setIsSending(true);
    setTimeout(() => {
      setIsSending(false);
      setSentSuccess(true);
      onSendSMS(subject.recipientName, subject.recipientPhone, message);
      setTimeout(() => {
        onClose();
      }, 1800);
    }, 700);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-tr from-pink-500/20 to-teal-500/20 border border-pink-500/40 text-pink-300">
              <MessageSquareHeart className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-100 text-sm">
                  Family Digest AI Translator
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-pink-500/10 text-pink-300 border border-pink-500/30">
                  GEMINI 2.5 FLASH
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Translating technical RPM sensors & vitals into a comforting 3-sentence family update.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          {/* Recipient & Senior Meta Header */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 text-xs">
            <div className="flex items-start gap-2.5">
              <div className="p-2 rounded-lg bg-slate-800 text-slate-300">
                <User className="h-4 w-4 text-teal-400" />
              </div>
              <div>
                <span className="text-[10px] font-mono uppercase text-slate-500 block">Senior Resident</span>
                <span className="font-bold text-slate-200 text-sm">
                  {subject.name} {subject.preferredName ? `("${subject.preferredName}")` : ''}
                </span>
                <span className="text-[11px] font-mono text-teal-400 block">{subject.locationOrRoom}</span>
              </div>
            </div>

            <div className="flex items-start gap-2.5 border-t sm:border-t-0 sm:border-l border-slate-800 pt-2 sm:pt-0 sm:pl-3">
              <div className="p-2 rounded-lg bg-slate-800 text-slate-300">
                <Phone className="h-4 w-4 text-pink-400" />
              </div>
              <div>
                <span className="text-[10px] font-mono uppercase text-slate-500 block">Family Contact</span>
                <span className="font-bold text-slate-200 text-sm">{subject.recipientName}</span>
                <span className="text-[11px] text-slate-400 block font-mono">
                  {subject.recipientRelation} • <strong className="text-slate-300">{subject.recipientPhone}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Technical RPM Sensor Feed Sources */}
          <div>
            <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 uppercase mb-2">
              <span className="flex items-center gap-1.5 font-bold text-slate-300">
                <Activity className="h-3.5 w-3.5 text-cyan-400" />
                Raw Telemetry Feeds Being Translated:
              </span>
              <span className="text-teal-400">Sub-mm Sensors Active</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
              <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800/80">
                <div className="flex items-center gap-1.5 text-indigo-400 text-[10px] font-mono uppercase mb-0.5">
                  <Moon className="h-3 w-3" /> Sleep Sensor Score
                </div>
                <div className="font-semibold text-slate-200 text-[11px] truncate">
                  {subject.sleepScoreOrState}
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800/80">
                <div className="flex items-center gap-1.5 text-rose-400 text-[10px] font-mono uppercase mb-0.5">
                  <Activity className="h-3 w-3" /> Biometric Vitals
                </div>
                <div className="font-semibold text-slate-200 text-[11px] truncate">
                  {subject.vitalsSummary}
                </div>
              </div>

              <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800/80">
                <div className="flex items-center gap-1.5 text-amber-400 text-[10px] font-mono uppercase mb-0.5">
                  <Utensils className="h-3 w-3" /> ADL / Meal Activity
                </div>
                <div className="font-semibold text-slate-200 text-[11px] truncate">
                  {subject.nutritionActivitySummary}
                </div>
              </div>
            </div>
          </div>

          {/* Simulated SMS Bubble & Editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[11px] font-bold text-pink-300 flex items-center gap-1.5">
                <Smartphone className="h-3.5 w-3.5 text-pink-400" />
                <span>Cellular SMS Preview (Family Recipient View)</span>
              </span>
              <button
                onClick={fetchDigest}
                disabled={isGenerating}
                className="text-[11px] text-teal-300 hover:text-teal-200 flex items-center gap-1 font-mono disabled:opacity-50"
              >
                <RotateCcw className={`h-3 w-3 ${isGenerating ? 'animate-spin' : ''}`} />
                <span>Re-generate</span>
              </button>
            </div>

            <div className="relative">
              {isGenerating ? (
                <div className="h-32 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center text-xs text-slate-400 gap-2">
                  <Sparkles className="h-4 w-4 text-pink-400 animate-spin" />
                  <span>Translating technical sleep & meal telemetry into comforting 3-sentence message...</span>
                </div>
              ) : (
                <div className="rounded-2xl bg-gradient-to-br from-slate-950 to-slate-900 border border-slate-800 p-4 relative shadow-inner">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full bg-teal-500 text-slate-950 font-bold flex items-center justify-center shrink-0 text-xs">
                      MC
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-300">MediCore Family Portal</span>
                        <span className="text-[10px] font-mono text-slate-500">Today • Just now</span>
                      </div>
                      <textarea
                        rows={4}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="w-full bg-slate-900/90 border border-slate-800 rounded-xl p-3 text-xs text-slate-100 focus:outline-none focus:border-teal-500 leading-relaxed font-sans"
                        placeholder="Family message text..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono px-1">
              <span>Standard SMS Encrypted Gateway (160 char units)</span>
              <span>3-Sentence Empathetic Standard</span>
            </div>
          </div>
        </div>

        {/* Modal Action Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between gap-3">
          <button
            onClick={handleCopy}
            disabled={!message || isGenerating}
            className="flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3.5 py-2 rounded-xl transition disabled:opacity-50"
          >
            {isCopied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-slate-400" />}
            <span>{isCopied ? 'Copied Text' : 'Copy SMS'}</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-xs text-slate-400 hover:text-white px-3 py-2 rounded-xl transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSend}
              disabled={!message || isGenerating || isSending || sentSuccess}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition shadow-lg ${
                sentSuccess
                  ? 'bg-emerald-600 text-white cursor-default'
                  : 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 shadow-teal-500/20'
              } disabled:opacity-50`}
            >
              {sentSuccess ? (
                <>
                  <Check className="h-4 w-4 text-white" />
                  <span>SMS Dispatched to {subject.recipientName}!</span>
                </>
              ) : isSending ? (
                <>
                  <Sparkles className="h-4 w-4 animate-spin text-slate-950" />
                  <span>Sending Cellular SMS...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>Send SMS to Family Contact ({subject.recipientPhone})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
